import React, { useEffect, useState } from "react";
import { RotateCcw, Smartphone, LayoutTemplate } from "lucide-react";
import { TAKEOVER_TOP, TAKEOVER_BOTTOM } from "@/components/CountdownTakeover";

const LOBBY_SECS = 5;
const COUNT_SECS = 10;
const TOTAL = LOBBY_SECS + COUNT_SECS + 2;
const ZONES = [
  { top: 0, h: TAKEOVER_TOP * 100, label: "Logo", cls: "bg-sky-500/30 border-sky-300" },
  { top: TAKEOVER_TOP * 100, h: (TAKEOVER_BOTTOM - TAKEOVER_TOP) * 100, label: "Hidden by race card", cls: "bg-red-500/40 border-red-300" },
  { top: TAKEOVER_BOTTOM * 100, h: (1 - TAKEOVER_BOTTOM) * 100, label: "Your message", cls: "bg-emerald-500/20 border-emerald-300" },
];

// Phone mockup replaying lobby teaser -> full-screen countdown takeover -> GO,
// with an optional safe-zone overlay for the 1080x2340 creative.
export const FeatureAdPreview = ({ src, className = "" }) => {
  const [t, setT] = useState(0);
  const [run, setRun] = useState(0);
  const [zones, setZones] = useState(false);

  useEffect(() => {
    if (!src) return undefined;
    setT(0);
    const id = setInterval(() => setT((x) => { if (x >= TOTAL) { clearInterval(id); return x; } return x + 1; }), 1000);
    return () => clearInterval(id);
  }, [src, run]);

  if (!src) return null;
  const phase = t < LOBBY_SECS ? "lobby" : t < LOBBY_SECS + COUNT_SECS ? "countdown" : t < TOTAL ? "go" : "done";
  const left = phase === "lobby" ? 15 - t : LOBBY_SECS + COUNT_SECS - t;
  const take = phase === "countdown" || zones;
  const label = zones ? "Anything in the red band is hidden during the countdown" : { lobby: "Lobby · small teaser", countdown: "Final 10s · full-screen takeover", go: "Green flag · ad hides", done: "Preview finished" }[phase];
  const count = left >= 4 ? String(left) : left === 3 ? "Ready" : left === 2 ? "Set" : "Go";

  return (
    <div className={`flex flex-col items-center gap-2 ${className}`} data-testid="feature-ad-preview">
      <div className="text-[10px] uppercase tracking-widest text-race-sub inline-flex items-center gap-1"><Smartphone size={11} /> Live phone preview</div>
      <div className="relative w-[200px] rounded-[28px] border-[6px] border-neutral-800 bg-race-bg shadow-2xl overflow-hidden" style={{ aspectRatio: "1080 / 2340" }}>
        {take && <img src={src} alt="Takeover preview" data-testid="feature-ad-preview-img" className="absolute inset-0 w-full h-full object-cover object-top" />}
        <div className="absolute top-1 left-1/2 -translate-x-1/2 w-14 h-3.5 rounded-full bg-black z-20" />
        {zones ? ZONES.map((z) => (
          <div key={z.label} className={`absolute inset-x-0 border-y border-dashed ${z.cls} grid place-items-center z-10`} style={{ top: `${z.top}%`, height: `${z.h}%` }}>
            <span className="text-[9px] font-semibold uppercase tracking-wider text-white bg-black/60 rounded px-1.5 py-0.5">{z.label}</span>
          </div>
        )) : (
          <div className="relative z-10 h-full flex flex-col px-2">
            <div className="shrink-0" style={{ height: take ? `${TAKEOVER_TOP * 100}%` : "10%" }} />
            {phase !== "go" && phase !== "done" ? (
              <div className="flex flex-col" style={take ? { height: `${(TAKEOVER_BOTTOM - TAKEOVER_TOP) * 100}%` } : undefined}>
                <div className="flex-1 flex flex-col bg-white rounded-lg border border-neutral-300 shadow-lg overflow-hidden">
                  <div className="flex justify-between px-2 py-1 border-b border-neutral-200 text-[7px] font-mono uppercase tracking-widest text-neutral-500"><span>Quick race</span><span>0.00s</span></div>
                  <div className="px-2 pt-2 text-[9px] font-mono text-neutral-400">Get ready…</div>
                  <div className="flex-1 grid place-items-center py-2 font-heading text-2xl text-neutral-900 leading-none" data-testid="feature-ad-preview-timer">{phase === "lobby" ? `0:${String(left).padStart(2, "0")}` : count}</div>
                </div>
                <div className="mt-1 bg-race-surface border border-race-line rounded px-1.5 py-1">
                  <div className="text-[6px] uppercase tracking-widest text-race-sub">Live positions</div>
                  <div className="mt-0.5 h-1 rounded bg-race-line" />
                </div>
              </div>
            ) : (
              <div className="flex-1 grid place-items-center text-race-volt font-heading text-xl">Racing…</div>
            )}
          </div>
        )}
      </div>
      <div className="text-[10px] text-race-muted text-center" data-testid="feature-ad-preview-phase">{label}</div>
      <div className="flex gap-2">
        <button type="button" onClick={() => { setZones(false); setRun((r) => r + 1); }} data-testid="feature-ad-preview-replay"
          className="inline-flex items-center gap-1.5 text-xs text-race-volt border border-race-volt/50 rounded-full px-3 py-1 hover:bg-race-volt/10 transition-colors">
          <RotateCcw size={12} /> Replay
        </button>
        <button type="button" onClick={() => setZones((z) => !z)} data-testid="feature-ad-preview-zones"
          className={`inline-flex items-center gap-1.5 text-xs border rounded-full px-3 py-1 transition-colors ${zones ? "bg-race-volt text-race-bg border-race-volt" : "text-race-sub border-race-line hover:text-race-txt"}`}>
          <LayoutTemplate size={12} /> Check my design
        </button>
      </div>
    </div>
  );
};

export default FeatureAdPreview;
